import { useState, useEffect, useMemo } from 'react';
// import { mockDocumentNumberRules } from '../../data/mockData.ts';
import { DocumentStatus } from '../../types/document.ts';
import { useActionPermission } from '../../hooks/useActionPermission.ts';
import { ArrowLeft, Upload, Save, X, Plus, Trash2, UserCircle, ChevronDown, ChevronUp, Users, GitBranch, WorkflowIcon } from 'lucide-react';
import { useNotify, useRedirect } from 'react-admin';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getAllUsers } from '../../services/userService.ts';
import { getAllTemplates, getStepsByTemplateId } from '../../services/workflowService.ts';
import { createDocument, updateDocument, getDocumentById, checkDocNumUnique, checkTitleUnique } from '../../services/documentService.ts';
import { fetchApprovers } from '../../services/approverService.ts';
import { create } from 'node:domain';

interface DocumentEditorProps {
  documentId: string | null;
  onBack: () => void;
}
interface ApprovalStep {
  id: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
  userId?: string;
  departmentTitle?: string;
  departmentTitleId?: string;
  selectorType?: 'user' | 'departmentTitle';
  selectorValue?: string;
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
}

export function DocumentManageCreate({ documentId, onBack }: DocumentEditorProps) {
  const notify = useNotify();
  const redirect = useRedirect();
  const { can } = useActionPermission();
  // workflow action: 控制是否可以修改审批流程（增删步骤、增删审批人）
  // 无此权限时只允许选择模板，不允许手动编辑工作流
  const canModifyWorkflow = can('documentslist:workflow', { menu: 'documentsList' });
  const currentUserId = Number(localStorage.getItem('userId'));
  const createdByValue = Number.isFinite(currentUserId) ? currentUserId : 0;
  const handleBackToList = () => {
    redirect('list', 'documentsList');
  };
  // Fetch approvers from userService API
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  // const [templates, setTemplates] = useState<ApprovalTemplate[]>([]);
  const [template, setTemplate] = useState<ApprovalTemplate>({
    Name: '',
    docType: 'SOP',
    description: '',
    steps: [],
    isActive: true,
    createdDate: new Date().toISOString().split('T')[0],
    createdBy: createdByValue,
    lastModified: new Date().toISOString().split('T')[0],
    usageCount: 0
  });

  const [templates, setTemplates] = useState<ApprovalTemplate[]>([]);
  // Load workflow templates from backend API
  useEffect(() => {
    const loadWorkflowTemplates = async () => {
      try {
        const allTemplates = await getAllTemplates();
        console.log('Raw templates from API:', allTemplates);
        // Transform backend template structure to ApprovalTemplate format
        const transformedTemplates: ApprovalTemplate[] = allTemplates.map((template: any) => ({
          id: template.id?.toString() || '',
          Name: template.name || template.templateName || '',
          docType: template.docType || template.documentType || '',
          description: template.description || '',
          isActive: template.isActive !== false,
          steps: (template.steps || []).map((step: any) => ({
            id: step.id?.toString() || '',
            stepNo: step.stepNo || step.stepNumber || 0,
            Name: step.name || step.stepName || '',
            stepType: step.stepType || 'single',
            approvers: (step.approverIds || step.approvers || []).map((approverId: any) => {
              // If approverIds is just an array of IDs, look up from approversPool
              if (typeof approverId === 'number' || typeof approverId === 'string') {
                const foundApprover = approversPool.find(a => a.id === approverId.toString());
                if (foundApprover) {
                  return foundApprover;
                }
                return {
                  id: approverId.toString(),
                  name: `User ${approverId}`,
                  role: 'User',
                  email: '',
                };
              }
              // Otherwise assume it's an approver object
              return {
                id: (approverId.userId ?? approverId.id)?.toString() || '',
                userId: (approverId.userId ?? approverId.id)?.toString() || '',
                name: approverId.name || approverId.userName || '',
                role: approverId.role || approverId.roleName || 'User',
                email: approverId.email || '',
              };
            }),
            approvalRule: step.approvalRule || 'all',
            timeLimit: step.timeLimit || 1,
            description: step.description || '',
            isOptional: step.isOptional === true,
          })),
          createdDate: template.createdDate || new Date().toISOString().split('T')[0],
          createdBy: template.createdBy || createdByValue,
          lastModified: template.lastModified || new Date().toISOString().split('T')[0],
          usageCount: template.usageCount || 0
        }));
        console.log('Transformed templates:', transformedTemplates);
        setTemplates(transformedTemplates);
      } catch (error) {
        console.error('Failed to load workflow templates:', error);
        setTemplates([]);
      }
    };

    loadWorkflowTemplates();
  }, [approversPool]);

  // const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [selectedDepartmentTitle, setSelectedDepartmentTitle] = useState<string>('all');
  const [approverKeyword, setApproverKeyword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(true);

  const buildDepartmentSelectorId = (title: string, departmentTitleId?: string) => {
  const normalizedId = String(departmentTitleId || '').trim();
  return normalizedId ? `deptid:${normalizedId}` : `dept:${title.trim()}`;
};
  // const notify = useNotify();
  const createDepartmentTitleApprover = (title: string, departmentTitleId?: string): Approver => {
    const normalized = title.trim();
    const normalizedId = String(departmentTitleId || '').trim();
    return {
      id: buildDepartmentSelectorId(normalized, normalizedId),
      userId: '',
      name: normalized,
      role: 'DepartmentTitle',
      email: '',
      departmentTitle: normalized,
      departmentTitleId: normalizedId,
      selectorType: 'departmentTitle',
      selectorValue: normalizedId || normalized
    };
  };
  const departmentTitleOptions = useMemo(() => {
    const map = new Map<string, { title: string; id: string }>();
    approversPool.forEach(approver => {
      const title = String(approver.departmentTitle || '').trim();
      if (!title) return;
      const id = String(approver.departmentTitleId || '').trim();
      if (!map.has(title)) {
        map.set(title, { title, id });
      } else if (!map.get(title)?.id && id) {
        map.set(title, { title, id });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [approversPool]);

  const selectedDepartmentTitleId = useMemo(() => {
    if (selectedDepartmentTitle === 'all') return '';
    return departmentTitleOptions.find(option => option.title === selectedDepartmentTitle)?.id || '';
  }, [departmentTitleOptions, selectedDepartmentTitle]);

  const currentStep = useMemo(() => {
    if (!currentStepId) return undefined;
    return template.steps.find(s => s.id === currentStepId);
  }, [currentStepId, template.steps]);

  const filteredApprovers = useMemo(() => {
    const keyword = approverKeyword.trim().toLowerCase();
    return approversPool.filter(approver => {
      const matchesDepartment =
        selectedDepartmentTitle === 'all' ||
        (approver.departmentTitle || '') === selectedDepartmentTitle;

      if (!matchesDepartment) return false;
      if (!keyword) return true;

      const target = `${approver.name} ${approver.email} ${approver.departmentTitle || ''}`.toLowerCase();
      return target.includes(keyword);
    });
  }, [approverKeyword, approversPool, selectedDepartmentTitle]);

  const closeApproverModal = () => {
    setShowApproverModal(false);
    setCurrentStepId(null);
    setSelectedDepartmentTitle('all');
    setApproverKeyword('');
  };
  useEffect(() => {
    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data.map((approver: any) => ({
          ...approver,
          departmentTitleId: String(approver.departmentTitleId || ''),
          userId: String(approver.id || ''),
          selectorType: 'user',
          selectorValue: String(approver.id || '')
        })));
      } catch (error) {
        console.error('Failed to load users:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    loadApprovers();
  }, [notify]);

  const handleDeleteStep = (stepId: string) => {
    const updatedTemplateSteps = template.steps
      .filter(step => step.id !== stepId)
      .map((step, idx) => ({ ...step, stepNo: idx + 1 }));
    const updatedApprovalSteps = approvalSteps
      .filter(step => step.id !== stepId)
      .map((step, idx) => ({ ...step, stepNo: idx + 1 }));

    setTemplate({
      ...template,
      steps: updatedTemplateSteps,
    });
    setApprovalSteps(updatedApprovalSteps);
  };

  const updateStepField = (stepId: string, field: keyof ApprovalStep, value: any) => {
    const updateSteps = (steps: ApprovalStep[]) => steps.map(step => (
      step.id === stepId ? { ...step, [field]: value } : step
    ));

    setTemplate(prev => ({
      ...prev,
      steps: updateSteps(prev.steps),
    }));
    setApprovalSteps(prev => updateSteps(prev));
  };

  const handleAddApprover = (stepId: string, approver: Approver) => {
    const addApproverToSteps = (steps: ApprovalStep[]) => steps.map(step => {
      if (step.id !== stepId) return step;
      const exists = step.approvers.some(a => a.id === approver.id);
      return exists ? step : { ...step, approvers: [...step.approvers, approver] };
    });

    setTemplate(prev => ({
      ...prev,
      steps: addApproverToSteps(prev.steps),
    }));
    setApprovalSteps(prev => addApproverToSteps(prev));
  };

  const handleRemoveApprover = (stepId: string, approverId: string) => {
    const removeApproverFromSteps = (steps: ApprovalStep[]) => steps.map(step => (
      step.id === stepId
        ? { ...step, approvers: step.approvers.filter(a => a.id !== approverId) }
        : step
    ));

    setTemplate(prev => ({
      ...prev,
      steps: removeApproverFromSteps(prev.steps),
    }));
    setApprovalSteps(prev => removeApproverFromSteps(prev));
  };


  // Load existing document if documentId provided
  const [existingDoc, setExistingDoc] = useState<any>(null);
  const [loadingDoc, setLoadingDoc] = useState(!!documentId);

  useEffect(() => {
    if (documentId) {
      const loadDocument = async () => {
        try {
          const doc = await getDocumentById(documentId);
          setExistingDoc(doc);
          setLoadingDoc(false);
        } catch (error: any) {
          console.error('Failed to load document:', error);
          notify(`Failed to load document: ${error.message}`, { type: 'error' });
          setLoadingDoc(false);
        }
      };
      loadDocument();
    } else {
      setLoadingDoc(false);
    }
  }, [documentId, notify]);

  const [formData, setFormData] = useState({
    title: existingDoc?.title || '',
    description: existingDoc?.description || '',
    category: existingDoc?.category || 'SOP',
    ownerId: (existingDoc as any)?.ownerId ? String((existingDoc as any).ownerId) : ((existingDoc as any)?.owner ? String((existingDoc as any).owner) : ''),
    versionChanges: '',
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<DocumentStatus>('Draft');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [approvalSteps, setApprovalSteps] = useState<ApprovalStep[]>([]);
  const [showApproverModal, setShowApproverModal] = useState(false);
  // const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [showApprovalSection, setShowApprovalSection] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docNum, setDocNum] = useState('');
  const [version, setVersion] = useState('1');
  const [revision, setRevision] = useState('1');
  const [validatingDocNum, setValidatingDocNum] = useState(false);
  const [titleValidation, setTitleValidation] = useState<{ status: 'idle' | 'checking' | 'valid' | 'invalid'; message?: string }>({ status: 'idle' });
  const [docNumValidation, setDocNumValidation] = useState<{ status: 'idle' | 'checking' | 'valid' | 'invalid'; message?: string }>({ status: 'idle' });

  // Update form data when existingDoc is loaded
  useEffect(() => {
    if (existingDoc) {
      setFormData({
        title: existingDoc.title || '',
        description: existingDoc.description || '',
        category: existingDoc.category || 'SOP',
        ownerId: (existingDoc as any).ownerId ? String((existingDoc as any).ownerId) : ((existingDoc as any).owner ? String((existingDoc as any).owner) : ''),
        versionChanges: '',
      });
      setStatus(existingDoc.status || 'Draft');
      setDocNum(existingDoc.docNum || existingDoc.documentNumber || '');
      setVersion(existingDoc.currentVersion || '1');
      setRevision(String(existingDoc.revision ?? 1));
    }
  }, [existingDoc]);

  useEffect(() => {
    const normalizedTitle = formData.title.trim();
    if (!normalizedTitle) {
      setTitleValidation({ status: 'idle' });
      return;
    }

    setTitleValidation({ status: 'checking' });
    const timer = setTimeout(async () => {
      try {
        const result = await checkTitleUnique(normalizedTitle, documentId || undefined);
        if (result.isValid) {
          setTitleValidation({ status: 'valid', message: 'Title is available' });
        } else {
          setTitleValidation({ status: 'invalid', message: result.message || 'Document title already exists' });
        }
      } catch (error: any) {
        setTitleValidation({ status: 'invalid', message: error?.message || 'Failed to validate title' });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.title, documentId]);

  useEffect(() => {
    const normalizedDocNum = docNum.trim();
    if (!normalizedDocNum) {
      setDocNumValidation({ status: 'idle' });
      return;
    }

    setDocNumValidation({ status: 'checking' });
    const timer = setTimeout(async () => {
      try {
        const result = await checkDocNumUnique(normalizedDocNum, documentId || undefined);
        if (result.isValid) {
          setDocNumValidation({ status: 'valid', message: 'DocNum is valid' });
        } else {
          setDocNumValidation({ status: 'invalid', message: result.message || 'DocNum validation failed' });
        }
      } catch (error: any) {
        setDocNumValidation({ status: 'invalid', message: error?.message || 'Failed to validate DocNum' });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [docNum, documentId]);

  const categories = ["SOP", "QMS", "Forms", "Policy", "SDS"];
  const selectedOwner = approversPool.find(user => user.id === formData.ownerId) || null;

  // Get available templates for selected category (from backend API)
  const availableTemplates = templates.filter(
    t => t.docType === formData.category && t.isActive
  );

  // Load template when selected
  useEffect(() => {
    if (selectedTemplateId) {
      const loadTemplateSteps = async () => {
        try {
          // First try to get steps from backend API
          const steps = await getStepsByTemplateId(selectedTemplateId);
          // Transform backend step structure to ApprovalStep format
                    const transformedSteps: ApprovalStep[] = (steps || []).map((step: any) => ({
                      id: step.id?.toString() || '',
                      stepNo: step.stepNo || step.stepNumber || 0,
                      Name: step.name || step.stepName || '',
                      stepType: step.stepType || 'single',
                      approvers: [
                        // Handle approvers
                        ...(step.approverIds || step.approvers || []).map((approverId: any) => {
                          // If approverIds is just an array of IDs, look up from approversPool
                          if (typeof approverId === 'number' || typeof approverId === 'string') {
                            const foundApprover = approversPool.find(a => a.id === approverId.toString());
                            if (foundApprover) {
                              return foundApprover;
                            }
                            return {
                              id: approverId.toString(),
                              name: `User ${approverId}`,
                              role: 'User',
                              email: '',
                              departmentTitle: '',
                            };
                          }
                          // Otherwise assume it's an approver object
                          const foundApprover = approversPool.find(a => a.id === (approverId.userId ?? approverId.id)?.toString());
                          if (foundApprover) {
                            return foundApprover;
                          }
                          return {
                            id: (approverId.userId ?? approverId.id)?.toString() || '',
                            userId: (approverId.userId ?? approverId.id)?.toString() || '',
                            name: approverId.name || approverId.userName || '',
                            role: approverId.role || approverId.roleName || 'User',
                            email: approverId.email || '',
                            departmentTitle: approverId.departmentTitle || '',
                          };
                        }),
                        // Handle departmentTitles
                        ...(step.departmentTitles || []).map((deptTitle: any) => ({
                          id: deptTitle.id?.toString() || `dept-${deptTitle.departmentTitleId}`,
                          userId: '',
                          name: deptTitle.departmentTitle || deptTitle.title || '',
                          role: 'DepartmentTitle',
                          email: '',
                          departmentTitle: deptTitle.departmentTitle || deptTitle.title || '',
                          departmentTitleId: deptTitle.departmentTitleId?.toString() || '',
                          selectorType: 'departmentTitle',
                          selectorValue: deptTitle.departmentTitleId?.toString() || deptTitle.departmentTitle || '',
                        }))
                      ],
                      approvalRule: step.approvalRule || 'all',
                      timeLimit: step.timeLimit || 1,
                      description: step.description || '',
                      isOptional: step.isOptional === true,
                    }));
                    setApprovalSteps(transformedSteps);
                    // Ensure the current `template` state also receives these steps
                    setTemplate(prev => ({ ...prev, steps: transformedSteps }));
        } catch (error) {
          console.error('Failed to load template steps:', error);
          // Fallback to steps from templates state
          const template = templates.find(t => t.id === selectedTemplateId);
          if (template) {
            const cloned = JSON.parse(JSON.stringify(template.steps || []));
            setApprovalSteps(cloned);
            setTemplate(prev => ({ ...prev, steps: cloned }));
          } else {
            setApprovalSteps([]);
            setTemplate(prev => ({ ...prev, steps: [] }));
          }
        }
      };
      loadTemplateSteps();
    } else {
      setApprovalSteps([]);
      setTemplate(prev => ({ ...prev, steps: [] }));
    }
  }, [selectedTemplateId, templates, approversPool]);

  // Reset template selection when category changes
  useEffect(() => {
    setSelectedTemplateId('');
    setApprovalSteps([]);
  }, [formData.category]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  // Add custom step
  const handleAddCustomStep = () => {
    const newStep: ApprovalStep = {
      id: `custom-${Date.now()}`,
      stepNo: approvalSteps.length + 1,
      Name: `Custom Step ${approvalSteps.length + 1}`,
      stepType: 'parallel',
      approvers: [],
      approvalRule: 'all',
      timeLimit: 2,
      description: '',
      isOptional: false
    };
    setApprovalSteps(prev => [...prev, newStep]);
    setTemplate(prev => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }));
  };

  const handleSubmit = async (e: React.FormEvent | null, statusParam?: DocumentStatus) => {
    if (e) e.preventDefault();
    const realStatus = statusParam || status;
    if (!formData.title.trim()) {
      notify('Document title is required', { type: 'warning' });
      return;
    }
    if (!docNum.trim()) {
      notify('DocNum is required', { type: 'warning' });
      return;
    }
    if (!formData.ownerId) {
      notify('Please select an owner', { type: 'warning' });
      return;
    }

    if (titleValidation.status === 'checking' || docNumValidation.status === 'checking') {
      notify('Validating title/doc number, please wait...', { type: 'info' });
      return;
    }

    if (titleValidation.status === 'invalid') {
      notify(titleValidation.message || 'Document title validation failed', { type: 'error' });
      return;
    }

    if (docNumValidation.status === 'invalid') {
      notify(docNumValidation.message || 'DocNum validation failed', { type: 'error' });
      return;
    }

    setValidatingDocNum(true);
    try {
      const [titleCheckResult, docNumCheckResult] = await Promise.all([
        checkTitleUnique(formData.title, documentId || undefined),
        checkDocNumUnique(docNum.trim(), documentId || undefined),
      ]);

      if (!titleCheckResult.isValid) {
        setTitleValidation({ status: 'invalid', message: titleCheckResult.message || 'Document title already exists' });
        notify(titleCheckResult.message || 'Document title validation failed', { type: 'error' });
        return;
      }

      if (!docNumCheckResult.isValid) {
        setDocNumValidation({ status: 'invalid', message: docNumCheckResult.message || 'DocNum validation failed' });
        notify(docNumCheckResult.message || 'DocNum validation failed', { type: 'error' });
        return;
      }

      setTitleValidation({ status: 'valid', message: 'Title is available' });
      setDocNumValidation({ status: 'valid', message: 'DocNum is valid' });
    } catch (validationError: any) {
      notify(`Failed to validate title/doc number: ${validationError.message}`, { type: 'error' });
      return;
    } finally {
      setValidatingDocNum(false);
    }
    if (realStatus === 'Review' && approvalSteps.length === 0) {
      notify('Please configure the approval workflow before submitting for review', { type: 'warning' });
      return;
    }
    if (realStatus === 'Review') {
      const stepsWithoutApprovers = approvalSteps.filter(s => s.approvers.length === 0 && !s.isOptional);
      if (stepsWithoutApprovers.length > 0) {
        const stepNames = stepsWithoutApprovers.map(s => s.Name).join(', ');
        notify(`The following required steps do not have approvers assigned: ${stepNames}`, { type: 'warning' });
        return;
      }
    }
    setSaving(true);
    try {
      const documentData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        ownerId: /^\d+$/.test(formData.ownerId) ? Number(formData.ownerId) : formData.ownerId,
        docNum,
        revision: Number(revision) || 1,
        version,
        file: uploadedFile || undefined,
        status: realStatus,
        approvalSteps: approvalSteps.length > 0
          ? approvalSteps.map(step => ({
            ...step,
            approvers: (step.approvers || []).map(approver => ({
              ...approver,
              id: String(approver.userId || approver.id),
              userId: String(approver.userId || approver.id),
            })),
          }))
          : undefined,
        documentNumber: docNum,
        currentVersion: version,
        createdBy: parseInt(localStorage.getItem('userId') || '1000'),
        templateId: selectedTemplateId || undefined,
      };
      let result;
      if (documentId) {
        result = await updateDocument(documentId, documentData);
      } else {
        result = await createDocument(documentData);
      }
      console.log('Document saved successfully:', result);
      const successMessage = `Document ${documentId ? 'updated' : 'created'} successfully!`;
      notify(successMessage, { type: 'success' });
      if (realStatus === 'Review' && approvalSteps.length > 0) {
        notify(`Approval workflow created with ${approvalSteps.length} steps`, { type: 'info' });
      }
      setTimeout(() => {
        redirect('list', 'documentsList');
      }, 1500);
    } catch (error: any) {
      console.error('Failed to save document:', error);
      notify(`Failed to save document: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsDraft = () => {
    handleSubmit(null, 'Draft');
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(2)} KB` : `${mb.toFixed(2)} MB`;
  };

  const getStepTypeBadge = (type: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      single: {
        label: 'Single',
        color: 'bg-blue-100 text-blue-800',
        icon: <UserCircle className="size-3" />
      },
      parallel: {
        label: 'Parallel',
        color: 'bg-purple-100 text-purple-800',
        icon: <Users className="size-3" />
      },
      conditional: {
        label: 'Conditional',
        color: 'bg-orange-100 text-orange-800',
        icon: <GitBranch className="size-3" />
      }
    };
    const config = configs[type] || configs.single;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  if (loadingDoc) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading document...</div>
        </div>
      </div>
    );
  }

  if (documentId && !existingDoc) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="mb-4 text-red-600 font-semibold">Document not found</div>
          <button
            type="button"
            onClick={handleBackToList}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 pt-5 pb-6 full-width">
      <form onSubmit={e => handleSubmit(e, status)}>
        {/* Header + Basic Information */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <button
                type="button"
                onClick={handleBackToList}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">
                {documentId ? 'Edit Document' : 'Create New Document'}
              </h2>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Basic Information</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter document title"
                />
                {titleValidation.status === 'checking' && (
                  <p className="text-xs text-gray-500 mt-1">Checking title...</p>
                )}
                {titleValidation.status === 'invalid' && (
                  <p className="text-xs text-red-600 mt-1">{titleValidation.message}</p>
                )}
                {titleValidation.status === 'valid' && (
                  <p className="text-xs text-green-600 mt-1">{titleValidation.message || 'Title is available'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Document Number uses DocNum value below
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner *
                </label>
                <Autocomplete
                  options={approversPool}
                  value={selectedOwner}
                  onChange={(_, value) => {
                    setFormData({ ...formData, ownerId: value?.id || '' });
                  }}
                  getOptionLabel={(option) => option.name || option.email || option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No users found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select owner"
                      size="small"
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: 42,
                      borderRadius: '0.5rem',
                    },
                  }}
                />
                {selectedOwner && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedOwner.role}{selectedOwner.email ? ` • ${selectedOwner.email}` : ''}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DocNum *
                  </label>
                  <input
                    type="text"
                    value={docNum}
                    onChange={(e) => setDocNum(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Please enter DocNum"
                  />
                  {docNumValidation.status === 'checking' && (
                    <p className="text-xs text-gray-500 mt-1">Checking DocNum...</p>
                  )}
                  {docNumValidation.status === 'invalid' && (
                    <p className="text-xs text-red-600 mt-1">{docNumValidation.message}</p>
                  )}
                  {docNumValidation.status === 'valid' && (
                    <p className="text-xs text-green-600 mt-1">{docNumValidation.message || 'DocNum is valid'}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Version
                    </label>
                    <input
                      type="text"
                      value={version}
                      onChange={(e) => setVersion(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default version is 1</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Revision
                    </label>
                    <input
                      type="text"
                      value={revision}
                      onChange={(e) => setRevision(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default revision is 1</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter document description"
                />
              </div>

              {documentId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Version Change Notes
                  </label>
                  <textarea
                    value={formData.versionChanges}
                    onChange={(e) => setFormData({ ...formData, versionChanges: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the main content of this revision"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document File</h3>

          {!uploadedFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">Click to upload or drag files here</p>
              <p className="text-sm text-gray-500 mb-4">Supported formats: PDF, Word, Excel, etc. Max 50MB</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                Choose File
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                />
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                  <Upload className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(uploadedFile.size)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Approval Workflow */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Approval Workflow Configuration</h3>
              <p className="text-sm text-gray-600 mt-1">Select an approval template or create a custom workflow</p>
            </div>
            <button
              type="button"
              onClick={() => setShowApprovalSection(!showApprovalSection)}
              className="text-gray-500 hover:text-gray-700"
            >
              {showApprovalSection ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
            </button>
          </div>

          {showApprovalSection && (
            <div className="space-y-4">
              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Approval Template
                </label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- No template (manual configuration) --</option>
                  {availableTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.Name} ({template.steps.length} steps)
                    </option>
                  ))}
                </select>
                {selectedTemplateId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Template loaded. You can adjust approvers as needed.
                  </p>
                )}
              </div>

              {/* Approval Steps */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Approval Steps</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{template.steps.length} step{template.steps.length !== 1 ? 's' : ''} configured</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCustomStep}
                    className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                  >
                    <Plus className="size-4" />
                    Add Step
                  </button>
                </div>

                {template.steps.length === 0 ? (
                  <div className="text-center py-14 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
                    <WorkflowIcon className="size-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium mb-1">No approval steps yet</p>
                    <p className="text-sm text-gray-400 mb-4">Add steps to define the approval workflow</p>
                    <button
                      type="button"
                      onClick={handleAddCustomStep}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                    >
                      <Plus className="size-4" />
                      Add First Step
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {template.steps.map((step, index) => (
                      <div key={step.id}>
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          {/* Step Header */}
                          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {step.stepNo}
                              </div>
                              <span className="font-semibold text-slate-800 text-sm">{step.Name || `Step ${step.stepNo}`}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${step.stepType === 'parallel' ? 'bg-purple-100 text-purple-800' :
                                  step.stepType === 'conditional' ? 'bg-orange-100 text-orange-800' :
                                    'bg-blue-100 text-blue-800'
                                }`}>
                                {step.stepType === 'parallel' ? 'Parallel' : step.stepType === 'conditional' ? 'Conditional' : 'Single'}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteStep(step.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="size-3.5" />
                              Delete
                            </button>
                          </div>

                          {/* Step Body */}
                          <div className="p-5">
                            <div className="grid grid-cols-2 gap-4 mb-5">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Step Name</label>
                                <input
                                  type="text"
                                  value={step.Name}
                                  onChange={(e) => updateStepField(step.id, 'Name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Step Type</label>
                                <select
                                  value={step.stepType}
                                  disabled
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm bg-gray-100 cursor-not-allowed"
                                >
                                  <option value="parallel">Parallel Approval</option>
                                </select>
                              </div>
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                                <textarea
                                  value={step.description}
                                  onChange={(e) => updateStepField(step.id, 'description', e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                              </div>
                            </div>

                            {/* Approvers */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">Approvers</label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentStepId(step.id);
                                    setSelectedDepartmentTitle('all');
                                    setApproverKeyword('');
                                    setShowApproverModal(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                                >
                                  <Plus className="size-3.5" />
                                  Add Approver
                                </button>
                              </div>
                              {step.approvers.length === 0 ? (
                                <div className="text-center py-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400">
                                  No approvers assigned yet
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {/* Department Titles */}
                                  {step.approvers.some(a => a.selectorType === 'departmentTitle') && (
                                    <div>
                                      <div className="text-xs font-semibold text-purple-600 mb-1.5 uppercase tracking-wide">Department Titles</div>
                                      <div className="flex flex-wrap gap-2">
                                        {step.approvers.filter(a => a.selectorType === 'departmentTitle').map(approver => (
                                          <div
                                            key={approver.id}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm"
                                          >
                                            <UserCircle className="size-4 text-purple-500 flex-shrink-0" />
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm">{approver.departmentTitle || approver.name}</div>
                                              <div className="text-xs text-purple-600">Dynamic by Department Title</div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveApprover(step.id, approver.id)}
                                              className="ml-1 text-gray-400 hover:text-red-600"
                                            >
                                              <X className="size-3.5" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Persons */}
                                  {step.approvers.some(a => a.selectorType !== 'departmentTitle') && (
                                    <div>
                                      <div className="text-xs font-semibold text-blue-600 mb-1.5 uppercase tracking-wide">Persons</div>
                                      <div className="flex flex-wrap gap-2">
                                        {step.approvers.filter(a => a.selectorType !== 'departmentTitle').map(approver => (
                                          <div
                                            key={approver.id}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm"
                                          >
                                            <UserCircle className="size-4 text-blue-500 flex-shrink-0" />
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm">{approver.name}</div>
                                              <div className="text-xs text-gray-500">{approver.departmentTitle || approver.role}</div>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveApprover(step.id, approver.id)}
                                              className="ml-1 text-gray-400 hover:text-red-600"
                                            >
                                              <X className="size-3.5" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {index < template.steps.length - 1 && (
                          <div className="flex justify-center py-2">
                            <div className="w-0.5 h-5 bg-gray-300 rounded"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>


            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <button
            type="button"
            onClick={handleBackToList}
            disabled={saving}
            className="flex-1 px-4 h-10 border border-gray-300 text-slate-700 rounded-[10px] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveAsDraft}
            disabled={saving || validatingDocNum}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            id="submit-btn"
            type="submit"
            className="hidden"
          >
            Submit
          </button>
        </div>
      </form>

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  type="button"
                  onClick={closeApproverModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              {isLoadingApprovers ? (
                <div className="text-center text-gray-600">Loading users...</div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Department Title
                        </label>
                        <select
                          value={selectedDepartmentTitle}
                          onChange={(e) => setSelectedDepartmentTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Department Titles</option>
                          {departmentTitleOptions.map(option => (
                            <option key={`${option.id}-${option.title}`} value={option.title}>{option.title}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Person
                        </label>
                        <input
                          type="text"
                          value={approverKeyword}
                          onChange={(e) => setApproverKeyword(e.target.value)}
                          placeholder="Search by name or email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">
                        {filteredApprovers.length} people available
                      </p>
                      <button
                        type="button"
                        disabled={selectedDepartmentTitle === 'all' || !selectedDepartmentTitle || !!currentStep?.approvers.some(a => a.id === buildDepartmentSelectorId(selectedDepartmentTitle, selectedDepartmentTitleId))}
                        onClick={() => {
                          if (selectedDepartmentTitle && selectedDepartmentTitle !== 'all' && currentStepId) {
                            handleAddApprover(currentStepId, createDepartmentTitleApprover(selectedDepartmentTitle, selectedDepartmentTitleId));
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-md text-xs font-semibold hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="size-3.5" />
                        Save Department Title
                      </button>
                    </div>
                  </div>

                  {filteredApprovers.length === 0 ? (
                    <div className="text-center text-sm text-gray-500 py-10 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                      No user matched the selected department/person condition.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                      {filteredApprovers.map(approver => {
                        const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id || (a.userId && a.userId === approver.id));

                        return (
                          <button
                            key={approver.id}
                            type="button"
                            onClick={() => {
                              if (!isAlreadyAdded && currentStepId) {
                                handleAddApprover(currentStepId, approver);
                              }
                            }}
                            disabled={isAlreadyAdded}
                            className={`p-4 border rounded-lg text-left transition-colors ${isAlreadyAdded
                                ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                                : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <UserCircle className="size-10 text-gray-400" />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{approver.name}</div>
                                <div className="text-sm text-gray-600">{approver.departmentTitle || approver.role}</div>
                                <div className="text-xs text-gray-500">{approver.email}</div>
                                <div className="mt-1 text-[11px] font-semibold text-blue-600">Person</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeApproverModal}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentManageCreate;